import { useEffect, useState, useMemo, Fragment } from "react";
import { ChevronDown, PackageSearch, Building2, Boxes, ShoppingCart, Banknote, Download, X, MapPin, Warehouse, Hash, List, LayoutGrid, CalendarDays, FileSpreadsheet, Truck, History, Pencil, Check, ChevronsUpDown, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Kpi, DetailActions, EmailHistory } from "../components/DetailActions";
import { mediaUrl } from "./ProductsPage";
import { fmtMoney, fmtDate, statusLabel, statusClass, unitLabel, detailTr } from "../lib/format";
import { Input } from "../components/ui/input";
import { SearchInput } from "../components/SearchInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { VariantBadge } from "../components/VariantBadge";
import { ServiceCalendar } from "../components/ServiceCalendar";
import { Checkbox } from "../components/ui/checkbox";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";

const pendingAgeClass = (d) =>
  d >= 14
    ? "border-red-400 bg-red-50 text-red-700"
    : d >= 7
    ? "border-orange-400 bg-orange-50 text-orange-700"
    : "border-amber-400 bg-amber-50 text-amber-800";

const dKey = (p) => `${p.sku || p.name || ""}|${p.variant || ""}`;

const ORD_NUM_KEYS = new Set(["qty", "lineTotal", "lineDelivered"]);

const OrgCombobox = ({ value, onChange, organisations, orgCountries }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const list = q.trim()
    ? organisations.filter((o) => `${o} ${orgCountries[o] || ""}`.toLocaleLowerCase("tr").includes(q.trim().toLocaleLowerCase("tr")))
    : organisations;
  const pick = (v) => { onChange(v); setOpen(false); setQ(""); };
  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-[210px] justify-between rounded-sm border-stone-300 bg-white px-3 font-normal" data-testid="by-product-organisation-select">
          <span className="truncate">{value === "__all__" ? `Tüm Müşteriler (${organisations.length})` : value}</span>
          <ChevronsUpDown size={14} className="ml-1 shrink-0 text-stone-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] rounded-sm p-0">
        <Command shouldFilter={false}>
          <CommandInput value={q} onValueChange={setQ} placeholder="Müşteri ara..." data-testid="organisation-search-input" />
          <CommandList className="max-h-72">
            <CommandEmpty>Müşteri bulunamadı</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all__" onSelect={() => pick("__all__")} data-testid="organisation-option-all">
                <Check size={14} className={`mr-2 shrink-0 ${value === "__all__" ? "opacity-100" : "opacity-0"}`} />
                Tüm Müşteriler ({organisations.length})
              </CommandItem>
              {list.map((o) => (
                <CommandItem key={o} value={o} onSelect={() => pick(o)} data-testid={`organisation-option-${o.slice(0, 25).replace(/\s+/g, "_")}`}>
                  <Check size={14} className={`mr-2 shrink-0 ${value === o ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate">{o}</span>
                  {orgCountries[o] && <span className="ml-1.5 shrink-0 text-[10px] text-stone-400">· {orgCountries[o]}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const LineDeliveryPopover = ({ o, unit, idx, onAdd, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const delivered = o.lineDelivered || 0;
  const remaining = Math.max(Math.round(((o.qty || 0) - delivered) * 100) / 100, 0);
  const submit = async () => {
    const q = parseFloat(String(val).replace(",", "."));
    if (!q || q <= 0) return toast.error("Geçerli bir miktar girin");
    setBusy(true);
    try {
      await onAdd(o, q);
      setVal("");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" title="Teslimat girişi" className="h-6 rounded-sm border-stone-300 px-1.5" data-testid={`line-deliver-btn-${idx}`}>
          <Plus size={11} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 rounded-sm p-3" data-testid={`line-deliver-popover-${idx}`}>
        <p className="text-xs font-bold text-[#0A0D0B]">{o.organisation}</p>
        <p className="mt-0.5 text-[11px] text-stone-500">
          Sipariş: {o.qty} {unitLabel(unit)} · Teslim: {delivered} ·{" "}
          <span className={remaining > 0 ? "font-semibold text-red-600" : "font-semibold text-green-700"}>Kalan: {remaining}</span>
        </p>
        {(o.lineDeliveryEntries || []).length > 0 && (
          <div className="mt-2 max-h-36 space-y-1 overflow-auto border-t border-stone-100 pt-2">
            {o.lineDeliveryEntries.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 text-[11px]" data-testid={`line-delivery-entry-${e.id}`}>
                <span className="font-medium text-stone-700">{e.qty} {unitLabel(unit)}</span>
                <span className="flex-1 truncate text-stone-400">{fmtDate(e.date)}{e.user ? ` · ${e.user}` : ""}</span>
                <button type="button" onClick={() => onDelete(o, e)} className="text-stone-400 hover:text-red-600" title="Sil" data-testid={`line-delivery-delete-${e.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 flex items-center gap-1.5">
          <Input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} placeholder={remaining > 0 ? `örn. ${remaining}` : "miktar"} className="h-8 rounded-sm border-stone-300 text-xs" data-testid={`line-deliver-input-${idx}`} />
          <Button size="sm" onClick={submit} disabled={busy} className="h-8 rounded-sm bg-[#1C4E35] px-3 text-xs font-bold text-white hover:bg-[#143A26]" data-testid={`line-deliver-save-${idx}`}>Ekle</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const MultiSelect = ({ options, values, onChange, allLabel, widthClass, testId }) => {
  const [open, setOpen] = useState(false);
  const label = values.length === 0 ? allLabel : values.length === 1 ? values[0] : `${values.length} seçili`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`flex h-10 items-center justify-between rounded-sm border border-stone-300 bg-white px-3 text-sm ${widthClass} ${values.length ? "text-stone-900" : "text-stone-600"}`}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown size={13} className="ml-2 shrink-0 text-stone-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command filter={(value, s) => (value.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Ara..." className="text-xs" data-testid={`${testId}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty>Bulunamadı.</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={() => onChange([])} className="text-xs font-medium" data-testid={`${testId}-all`}>
                <Check size={12} className={`mr-1.5 shrink-0 ${values.length === 0 ? "opacity-100 text-[#1C4E35]" : "opacity-0"}`} />
                {allLabel}
              </CommandItem>
              {options.map((o) => {
                const on = values.includes(o);
                return (
                  <CommandItem
                    key={o}
                    value={o}
                    onSelect={() => onChange(on ? values.filter((x) => x !== o) : [...values, o])}
                    className="text-xs"
                    data-testid={`${testId}-option-${o.replace(/\s+/g, "_")}`}
                  >
                    <span className={`mr-2 flex h-3.5 w-3.5 shrink-0 items-center justify-center border ${on ? "border-[#1C4E35] bg-[#1C4E35] text-white" : "border-stone-300 bg-white"}`}>
                      {on && <Check size={10} />}
                    </span>
                    <span className="truncate">{o}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

const StatusKpis = ({ spaceType, organisation, currency, zone, status, search, kind, category, subCategory, country, onToggle, hideMoney }) => {
  const [data, setData] = useState(null);
  const catKey = (category || []).join("||");
  const subKey = (subCategory || []).join("||");
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/orders/status-summary", {
        params: {
          space_type: spaceType || undefined,
          organisation: organisation !== "__all__" ? organisation : undefined,
          currency: currency !== "__all__" ? currency : undefined,
          zone: zone || undefined,
          search: search || undefined,
          kind: kind && kind !== "__all__" ? kind : undefined,
          category: catKey || undefined,
          sub_category: subKey || undefined,
          country: country && country !== "__all__" ? country : undefined,
        },
      }).then((r) => setData(r.data)).catch(() => {});
    }, search ? 350 : 0);
    return () => clearTimeout(t);
  }, [spaceType, organisation, currency, zone, search, kind, catKey, subKey, country]);
  if (!data) return null;
  const m = data.match;
  const strip = m && search ? (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border border-[#1C4E35]/40 bg-[#1C4E35]/5 px-3 py-2" data-testid="search-match-strip">
      <span className="font-heading text-sm font-black tracking-tight text-[#0A0D0B]" data-testid="search-match-org">{m.organisation}</span>
      {m.plots.length > 0 && (
        <span className="text-xs text-stone-700" data-testid="search-match-plot">
          <span className="text-stone-500">Stant No:</span> <strong className="text-[#1C4E35]">{m.plots.join(", ")}</strong>
        </span>
      )}
      {m.zones.length > 0 && (
        <span className="text-xs text-stone-700" data-testid="search-match-zone">
          <span className="text-stone-500">Zone:</span> {m.zones.map((z) => `${z.charAt(0).toUpperCase() + z.slice(1)} Zone`).join(", ")}
        </span>
      )}
      {m.halls.length > 0 && (
        <span className="text-xs text-stone-700" data-testid="search-match-hall">
          <span className="text-stone-500">Hol:</span> {m.halls.join(", ")}
        </span>
      )}
      {m.country && (
        <span className="text-xs text-stone-700" data-testid="search-match-country">
          <span className="text-stone-500">Ülke:</span> {m.country}
        </span>
      )}
      {m.spaces.length > 0 && (
        <span className="text-xs text-stone-500" data-testid="search-match-space">{m.spaces.join(" · ")}</span>
      )}
    </div>
  ) : null;
  if (data.data.length === 0) {
    return search ? (
      <div className="border border-stone-200 bg-white px-3 py-2 text-xs text-stone-500" data-testid="search-no-match">
        "{search}" aramasıyla eşleşen sipariş bulunamadı
      </div>
    ) : null;
  }
  const allTotals = {};
  data.data.forEach((x) => {
    if (x.status === "CANCELLED") return;
    Object.entries(x.totals).forEach(([c, v]) => { allTotals[c] = (allTotals[c] || 0) + v; });
  });
  const allActive = status === "__all__";
  return (
    <div className="space-y-2">
      {strip}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="status-kpi-cards">
      <button
        onClick={() => onToggle("__all__")}
        data-testid="status-kpi-ALL"
        title="Tüm durumları göster"
        className={`border bg-white p-3 text-left transition-colors duration-150 hover:border-[#1C4E35]/60 active:scale-[0.98] ${
          allActive ? "border-[#1C4E35] ring-1 ring-[#1C4E35]" : "border-stone-200"
        }`}
      >
        <span className="inline-block border border-[#1C4E35]/40 bg-[#1C4E35]/10 px-2 py-0.5 text-[10px] font-medium text-[#1C4E35]">Tümü</span>
        <p className="mt-2 font-heading text-xl font-black tracking-tight text-[#0A0D0B]">
          {data.orderCount} <span className="text-xs font-medium text-stone-500">sipariş</span>
        </p>
        {!hideMoney && (
          <p className="truncate text-xs text-stone-600" data-testid="status-kpi-total-ALL">
            {Object.entries(allTotals).map(([c, v]) => fmtMoney(v, c)).join(" + ") || "—"}
          </p>
        )}
      </button>
      {data.data.map((x) => {
        const active = status === x.status;
        return (
          <button
            key={x.status}
            onClick={() => onToggle(x.status)}
            data-testid={`status-kpi-${x.status}`}
            title={active ? "Filtreyi kaldır" : `Sadece '${statusLabel(x.status)}' siparişleri göster`}
            className={`border bg-white p-3 text-left transition-colors duration-150 hover:border-[#1C4E35]/60 active:scale-[0.98] ${
              active ? "border-[#1C4E35] ring-1 ring-[#1C4E35]" : "border-stone-200"
            }`}
          >
            <span className={`inline-block border px-2 py-0.5 text-[10px] font-medium ${statusClass(x.status)}`}>{statusLabel(x.status)}</span>
            <p className="mt-2 font-heading text-xl font-black tracking-tight text-[#0A0D0B]">
              {x.count} <span className="text-xs font-medium text-stone-500">sipariş</span>
            </p>
            {!hideMoney && (
              <p className="truncate text-xs text-stone-600" data-testid={`status-kpi-total-${x.status}`}>
                {Object.entries(x.totals).map(([c, v]) => fmtMoney(v, c)).join(" + ") || "—"}
              </p>
            )}
          </button>
        );
      })}
      </div>
    </div>
  );
};

const SPACE_TYPE_TR = { delegation: "Ofisler", pavilion: "Pavilyonlar", stand: "Stantlar" };

const beigeStyle = (variant) => (/beige|bej/i.test(variant || "") ? { filter: "sepia(0.55) saturate(1.5) brightness(1.12) hue-rotate(-8deg)" } : undefined);

const CustomerKpiPanel = ({ organisation, status, spaceType, kind, country, onStatusToggle, hideMoney, onlyDelivered }) => {
  const [s, setS] = useState(null);
  const activeStatus = status !== "__all__" ? status : "";
  const activeKind = kind !== "__all__" ? kind : "";
  const activeCountry = country && country !== "__all__" ? country : "";

  useEffect(() => {
    setS(null);
  }, [organisation]);

  useEffect(() => {
    api.get("/orders/customer/summary", { params: { organisation, status: activeStatus || undefined, space_type: spaceType || undefined, kind: activeKind || undefined, country: activeCountry || undefined } }).then((r) => setS(r.data)).catch(() => {});
  }, [organisation, activeStatus, spaceType, activeKind, activeCountry]);

  if (!s) return null;
  const totalStr = Object.entries(s.totals).map(([c, v]) => fmtMoney(v, c)).join(" + ");

  const customerExcel = async () => {
    try {
      const res = await api.get("/orders/customer/excel", {
        params: { organisation, status: activeStatus || undefined, space_type: spaceType || undefined, kind: activeKind || undefined, country: activeCountry || undefined },
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i);
      const name = m ? decodeURIComponent(m[1]) : `cop31_musteri_${organisation}.xlsx`.replace(/\s+/g, "_");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi");
    } catch {
      toast.error("Excel oluşturulamadı");
    }
  };
  return (
    <div className="fade-up border border-[#1C4E35]/30 bg-white p-4" data-testid="customer-kpi-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="font-heading text-base font-black tracking-tight text-[#0A0D0B]" data-testid="customer-kpi-name">
          {s.organisation}
          {spaceType && SPACE_TYPE_TR[spaceType] && (
            <span className="ml-2 border border-[#1C4E35]/40 bg-[#1C4E35]/5 px-1.5 py-0.5 align-middle text-[10px] font-semibold text-[#1C4E35]" data-testid="customer-kpi-space-type">
              Sadece {SPACE_TYPE_TR[spaceType]}
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap gap-1.5" data-testid="customer-kpi-statuses">
            {s.statusCounts.map((x) => {
              const isActive = activeStatus === x.status;
              const isPendingAged = x.status === "PENDING" && s.pendingMaxAgeDays != null;
              const cls = isPendingAged ? pendingAgeClass(s.pendingMaxAgeDays) : statusClass(x.status);
              return (
                <button
                  key={x.status}
                  onClick={() => onStatusToggle(x.status)}
                  data-testid={`customer-status-badge-${x.status}`}
                  title={isActive ? "Filtreyi kaldır" : `Sadece '${statusLabel(x.status)}' siparişleri göster`}
                  className={`cursor-pointer border px-2 py-0.5 text-[10px] font-medium transition-opacity duration-150 hover:opacity-75 active:scale-95 ${cls} ${
                    isActive ? "ring-2 ring-[#1C4E35] ring-offset-1" : ""
                  }`}
                >
                  {statusLabel(x.status)}: {x.count}
                  {isPendingAged ? ` · ${s.pendingMaxAgeDays} gün` : ""}
                </button>
              );
            })}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={customerExcel}
            data-testid="customer-excel-button"
            className="h-8 rounded-sm border-stone-300 text-xs hover:bg-stone-100 active:scale-95"
          >
            <FileSpreadsheet size={13} className="mr-1.5" /> Excel'e Aktar
          </Button>
          <DetailActions
            pdfPath="/orders/customer/pdf"
            pdfParams={{ organisation, status: activeStatus || undefined, space_type: spaceType || undefined, kind: activeKind || undefined, country: activeCountry || undefined, only_delivered: onlyDelivered ? 1 : undefined }}
            showDeliveredFilter
            emailPath={hideMoney ? undefined : "/orders/customer/email"}
            emailPayload={hideMoney ? undefined : { organisation, status: activeStatus || "", space_type: spaceType || "", kind: activeKind || "", country: activeCountry || "" }}
            filename={`cop31_musteri_${organisation}${spaceType && SPACE_TYPE_TR[spaceType] ? `_${SPACE_TYPE_TR[spaceType]}` : ""}.pdf`.replace(/\s+/g, "_")}
            testPrefix="customer"
            historyScope={hideMoney ? undefined : `customer|${organisation}`}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi icon={ShoppingCart} label="Sipariş" value={s.orderCount} testId="customer-kpi-orders" />
        <Kpi icon={Boxes} label="Toplam Miktar" value={s.totalQty} testId="customer-kpi-qty" />
        {!hideMoney && <Kpi icon={Banknote} label="Toplam Değer" value={totalStr || "—"} testId="customer-kpi-total" />}
        <Kpi icon={Hash} label="Stant No" value={s.plots.length ? s.plots.join(", ") : "Not set"} testId="customer-kpi-plot" />
        <Kpi icon={MapPin} label="Zone" value={s.zones.length ? s.zones.map((z) => `${z.charAt(0).toUpperCase() + z.slice(1)} Zone`).join(", ") : "Not set"} testId="customer-kpi-zone" />
        <Kpi icon={Warehouse} label="Hol" value={s.halls.length ? s.halls.join(", ") : "Not set"} testId="customer-kpi-hall" />
      </div>
      {(s.spacePlots?.length > 0 || s.spaces.length > 0) && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="customer-kpi-spaces">
          <span className="text-[11px] text-stone-500">Alanlar:</span>
          {(s.spacePlots?.length ? s.spacePlots : s.spaces.map((sp) => ({ space: sp }))).map((x, i) => (
            <span key={i} className="border border-stone-300 bg-stone-50 px-1.5 py-0.5 text-[11px] text-stone-600" data-testid={`customer-kpi-space-${i}`}>
              {x.space || "—"}
              {x.plot && <span className="font-bold text-[#1C4E35]"> · Stant: {x.plot}</span>}
              {x.country && <span className="text-stone-400"> · {x.country}</span>}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const procureChip = (p) => {
  const total = p.procuredQty || 0;
  const d = p.directQty || 0;
  const s = Math.max(0, total - d);
  const rem = p.qty - total;
  const text =
    rem < 0
      ? `Tedarikçi: ${total}/${p.qty} · Fazla +${-rem}`
      : rem > 0
      ? `Tedarikçi: ${s}/${p.qty} · Kalan ${rem}`
      : d === 0 || s > 0
      ? `Tedarikçi Atandı: ${s}/${p.qty} ✓`
      : `Doğrudan Satınalma: ${total}/${p.qty} ✓`;
  const cls =
    p.qty > 0 && total >= p.qty
      ? total - d <= 0 && d > 0
        ? "border-sky-400 bg-sky-50 text-sky-700"
        : "border-green-500 bg-green-50 text-green-700"
      : total > 0
      ? "border-amber-400 bg-amber-50 text-amber-800"
      : "border-stone-300 bg-stone-50 text-stone-500";
  return { text, cls };
};

export default function OrdersByProductPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [spaceType, setSpaceType] = useState("");
  const [kind, setKind] = useState("__all__");
  const [category, setCategory] = useState([]);
  const [subCategory, setSubCategory] = useState([]);
  const [status, setStatus] = useState("__all__");
  const [statuses, setStatuses] = useState([]);
  const [currency, setCurrency] = useState("__all__");
  const [currencies, setCurrencies] = useState([]);
  const [organisation, setOrganisation] = useState("__all__");
  const [organisations, setOrganisations] = useState([]);
  const [orgCountries, setOrgCountries] = useState({});
  const [country, setCountry] = useState("__all__");
  const [countries, setCountries] = useState([]);
  const [zone, setZone] = useState("");
  const [zones, setZones] = useState([]);

  useEffect(() => {
    api.get("/filters", {
      params: {
        space_type: spaceType || undefined,
        status: status !== "__all__" ? status : undefined,
        currency: currency !== "__all__" ? currency : undefined,
        organisation: organisation !== "__all__" ? organisation : undefined,
        zone: zone || undefined,
        country: country !== "__all__" ? country : undefined,
      },
    }).then((r) => {
      const d = r.data;
      setStatuses(d.statuses || []);
      setCurrencies(d.currencies || []);
      setOrganisations(d.organisations || []);
      setOrgCountries(d.orgCountries || {});
      setCountries(d.countries || []);
      setZones((z) => (z.length ? z : d.zones || []));
      setStatus((s) => (s !== "__all__" && !(d.statuses || []).includes(s) ? "__all__" : s));
      setCurrency((c) => (c !== "__all__" && !(d.currencies || []).includes(c) ? "__all__" : c));
      setOrganisation((o) => (o !== "__all__" && !(d.organisations || []).includes(o) ? "__all__" : o));
      setCountry((c) => (c !== "__all__" && !(d.countries || []).some((x) => x.code === c) ? "__all__" : c));
    }).catch(() => {});
  }, [spaceType, status, currency, organisation, zone, country]);
  const [expandedCats, setExpandedCats] = useState({});
  const [catHistory, setCatHistory] = useState(null);
  const [selected, setSelected] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [mailRefresh, setMailRefresh] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const [catAssign, setCatAssign] = useState(null);
  const [catChecked, setCatChecked] = useState({});
  const [catSupplier, setCatSupplier] = useState("");
  const { user } = useAuth();
  const isCatOpen = (cat) => !!expandedCats[cat] || !!search;
  const isAdmin = user?.role === "admin";
  const canAssign = isAdmin || user?.role === "satinalma";
  const canReceive = isAdmin || user?.role === "malkabul";

  const [ordSort, setOrdSort] = useState(null);
  useEffect(() => { setOrdSort(null); }, [selected?.sku]);
  const sortedOrderers = useMemo(() => {
    if (!selected?.orderers) return [];
    if (!ordSort) return selected.orderers;
    const mul = ordSort.dir === "asc" ? 1 : -1;
    return [...selected.orderers].sort((a, b) =>
      ORD_NUM_KEYS.has(ordSort.key)
        ? ((a[ordSort.key] || 0) - (b[ordSort.key] || 0)) * mul
        : String(a[ordSort.key] || "").localeCompare(String(b[ordSort.key] || ""), "tr") * mul);
  }, [selected, ordSort]);

  const matchOrd = (x, o) => x.ref === o.ref && x.lineIdx === o.lineIdx && (x.variant || "") === (o.variant || "") && (x.detail || "") === (o.detail || "");

  const addLineDelivery = async (o, qty) => {
    try {
      const r = await api.post("/line-deliveries", { sku: selected.sku, ref: o.ref, variant: o.variant || null, detail: o.detail || null, line_idx: o.lineIdx || 0, organisation: o.organisation || "", qty });
      setSelected((prev) => (prev ? { ...prev, orderers: prev.orderers.map((x) => (matchOrd(x, o) ? { ...x, lineDelivered: (x.lineDelivered || 0) + qty, lineDeliveryEntries: [...(x.lineDeliveryEntries || []), r.data] } : x)) } : prev));
      toast.success(`${qty} ${unitLabel(selected.unit)} teslimat girildi`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Teslimat girilemedi");
    }
  };

  const deleteLineDelivery = async (o, entry) => {
    try {
      await api.delete(`/line-deliveries/${entry.id}`);
      setSelected((prev) => (prev ? { ...prev, orderers: prev.orderers.map((x) => (matchOrd(x, o) ? { ...x, lineDelivered: Math.max((x.lineDelivered || 0) - (entry.qty || 0), 0), lineDeliveryEntries: (x.lineDeliveryEntries || []).filter((e) => e.id !== entry.id) } : x)) } : prev));
      toast.success("Teslimat kaydı silindi");
    } catch {
      toast.error("Silinemedi");
    }
  };

  const fullDeliverToggle = async (o, checked) => {
    if (checked) {
      const remaining = Math.round(((o.qty || 0) - (o.lineDelivered || 0)) * 100) / 100;
      if (remaining <= 0) return;
      await addLineDelivery(o, remaining);
    } else {
      const entries = o.lineDeliveryEntries || [];
      if (!entries.length) return;
      try {
        await Promise.all(entries.map((e) => api.delete(`/line-deliveries/${e.id}`)));
        setSelected((prev) => (prev ? { ...prev, orderers: prev.orderers.map((x) => (matchOrd(x, o) ? { ...x, lineDelivered: 0, lineDeliveryEntries: [] } : x)) } : prev));
        toast.success("Teslimat kayıtları kaldırıldı");
      } catch {
        toast.error("Kaldırılamadı");
      }
    }
  };

  const SortHead = ({ k, label, right }) => (
    <TableHead
      onClick={() => setOrdSort((s) => (s?.key === k ? (s.dir === "asc" ? { key: k, dir: "desc" } : null) : { key: k, dir: "asc" }))}
      className={`cursor-pointer select-none text-xs hover:text-[#1C4E35] ${right ? "text-right" : ""}`}
      title="Sırala"
      data-testid={`ord-sort-${k}`}
    >
      {label}{ordSort?.key === k ? (ordSort.dir === "asc" ? " ▲" : " ▼") : ""}
    </TableHead>
  );
  const isStaff = isAdmin || user?.role === "satinalma" || user?.role === "malkabul";
  const isViewer = user?.role === "izleyici";
  const isLimited = user?.role === "satinalma" || user?.role === "malkabul" || isViewer;
  const [suppliers, setSuppliers] = useState([]);
  const [procureDialog, setProcureDialog] = useState(null);
  const [procureSupplier, setProcureSupplier] = useState("");
  const [directBuy, setDirectBuy] = useState(false);
  const [freeVendor, setFreeVendor] = useState("");
  const [assignQty, setAssignQty] = useState("");
  const [assignSupplier, setAssignSupplier] = useState("");
  const [assignDirect, setAssignDirect] = useState(false);
  const [assignVendor, setAssignVendor] = useState("");
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  const [showVendorList, setShowVendorList] = useState(false);
  const [showFreeVendorList, setShowFreeVendorList] = useState(false);

  useEffect(() => {
    if (canAssign) api.get("/procurement/direct-vendors").then((r) => setVendorSuggestions(r.data.vendors || [])).catch(() => {});
  }, [canAssign]);

  const [adminDQty, setAdminDQty] = useState("");
  const [adminDDate, setAdminDDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [editDQty, setEditDQty] = useState("");
  const [editDDate, setEditDDate] = useState("");
  const [confirmBox, setConfirmBox] = useState(null);

  const askConfirm = (title, message, onConfirm) => setConfirmBox({ title, message, onConfirm });

  const saveDeliveryEdit = async (d) => {
    try {
      await api.post("/procurement/delivery-update", { id: d.id, qty: Number(editDQty), date: editDDate });
      toast.success("Teslimat kaydı güncellendi");
      setEditingDelivery(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Güncellenemedi");
    }
  };

  const deleteDeliveryEntry = (d) => {
    askConfirm("Teslimat kaydını sil", `${d.qty} adetlik teslimat kaydını silmek istediğinize emin misiniz?`, async () => {
      try {
        await api.delete(`/procurement/delivery/${d.id}`);
        toast.success("Teslimat kaydı silindi");
        setRefreshTick((t) => t + 1);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Silinemedi");
      }
    });
  };

  const [receiveQty, setReceiveQty] = useState("");
  const [receiveDate, setReceiveDate] = useState("");

  const clearAllDeliveries = () => {
    askConfirm("Tüm teslimat kayıtlarını sil", `${selected.name} ürünündeki TÜM teslimat kayıtları (${(selected.deliveryEntries || []).length} kayıt, ${selected.deliveredQty || 0} ${unitLabel(selected.unit)}) silinecek. Emin misiniz?`, async () => {
      try {
        const r = await api.post("/procurement/delivery-clear", { sku: selected.sku, variant: selected.variant || null });
        toast.success(`${r.data.removed} teslimat kaydı silindi`);
        setRefreshTick((t) => t + 1);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Silinemedi");
      }
    });
  };

  const saveDirectDelivery = async () => {
    try {
      await api.post("/procurement/direct-delivery", { sku: selected.sku, variant: selected.variant || null, name: selected.name, qty: Number(adminDQty), date: adminDDate });
      toast.success(`${Number(adminDQty)} adet depoya teslim alındı olarak kaydedildi`);
      setAdminDQty("");
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Teslim girişi yapılamadı");
    }
  };

  const closeProductModal = () => {
    const doClose = () => {
      setAssignQty("");
      setAssignSupplier("");
      setAssignDirect(false);
      setAssignVendor("");
      setShowVendorList(false);
      setSelected(null);
    };
    if (assignDirect && (assignQty || assignVendor.trim() || assignSupplier)) {
      askConfirm("Kaydedilmemiş bilgiler", "Doğrudan satınalma bilgilerini kaydetmediniz. Kaydetmeden çıkmak istediğinize emin misiniz?", doClose);
      return;
    }
    doClose();
  };

  const requestQtyAssign = () => {
    const kalan = Math.max(0, selected.qty - (selected.procuredQty || 0));
    const q = Number(assignQty);
    if (q > kalan) {
      askConfirm(
        "Sipariş sayısını aşıyorsunuz",
        `Toplam sipariş miktarını ${q - kalan} adet aşıyorsunuz (kalan ${kalan}). Fazlası "sipariş fazlası" olarak kaydedilecek. Devam etmek istediğinize emin misiniz?`,
        confirmQtyAssign
      );
      return;
    }
    confirmQtyAssign();
  };

  const confirmQtyAssign = async () => {
    const supplierName = suppliers.find((s) => s.id === assignSupplier)?.name || (assignDirect ? assignVendor.trim() || "Doğrudan Satınalma" : "");
    try {
      const r = await api.post("/procurement/assign-qty", {
        sku: selected.sku, variant: selected.variant || null, name: selected.name,
        supplier_id: assignSupplier || undefined, qty: Number(assignQty),
        direct: assignDirect, free_vendor: assignDirect && !assignSupplier ? assignVendor.trim() || undefined : undefined,
      });
      toast.success(assignDirect
        ? `${Number(assignQty)} adet ${supplierName} — doğrudan satınalma olarak kaydedildi${r.data.surplus > 0 ? ` · Sipariş fazlası +${r.data.surplus}` : ""}`
        : `${Number(assignQty)} adet ${supplierName} tedarikçisine atandı${r.data.surplus > 0 ? ` · Sipariş fazlası +${r.data.surplus}` : ""}${r.data.notified ? " · Tedarikçiye e-posta gönderildi" : ""}`);
      setAssignQty("");
      setAssignSupplier("");
      setAssignDirect(false);
      if (assignDirect && !assignSupplier && assignVendor.trim()) {
        setVendorSuggestions((v) => (v.includes(assignVendor.trim()) ? v : [...v, assignVendor.trim()].sort((a, b) => a.localeCompare(b, "tr"))));
      }
      setAssignVendor("");
      setShowVendorList(false);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Atama yapılamadı");
    }
  };

  const removeAllMarks = () => {
    const hasDeliveries = (selected.deliveredQty || 0) > 0;
    askConfirm(
      "Tüm tedarik işaretlerini kaldır",
      `Bu üründeki TÜM tedarik işaretleri (tedarikçi atamaları ve doğrudan satınalmalar) kaldırılacak${hasDeliveries ? " ve tüm teslimat kayıtları iptal edilecek" : ""}. Emin misiniz?\n\nKaldırdığınız zaman tedarik edilmiş olarak gösterilen ürünlerin tedarik durumunu sıfırlayacaktır.`,
      async () => {
        try {
          const r = await api.post("/procurement/unassign-product", { sku: selected.sku, variant: selected.variant || null, reset_deliveries: true });
          toast.success(`${r.data.removed} tedarik işareti kaldırıldı${hasDeliveries ? " · Teslimat kayıtları iptal edildi" : ""}`);
          setRefreshTick((t) => t + 1);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Kaldırılamadı");
        }
      }
    );
  };

  const removeProductAssign = async (p) => {
    try {
      const r = await api.post("/procurement/unassign-product", { sku: p.sku, variant: p.variant || null });
      toast.success(`${p.name} için ${r.data.removed} atama kaldırıldı`);
      setCatAssign((c) => (c ? { ...c, products: c.products.map((x) => (x.sku === p.sku && (x.variant || "") === (p.variant || "") ? { ...x, procuredQty: 0, directQty: 0, supplierBreakdown: [] } : x)) } : c));
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Atama kaldırılamadı");
    }
  };

  const acceptDeliveryEntry = async (d) => {
    try {
      await api.post("/procurement/delivery-accept", { id: d.id });
      toast.success("Mal kabul onaylandı");
      setSelected((s) => (s ? { ...s, deliveryEntries: (s.deliveryEntries || []).map((x) => (x.id === d.id ? { ...x, status: "accepted" } : x)) } : s));
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Onaylanamadı");
    }
  };

  const removeAllCatAssigns = () => {
    const assigned = (catAssign?.products || []).filter((p) => (p.procuredQty || 0) > 0);
    if (!assigned.length) return;
    const hasDeliveries = assigned.some((p) => (p.deliveredQty || 0) > 0);
    askConfirm(
      "Tüm atamaları kaldır",
      `${catAssign.category} kategorisindeki ${assigned.length} üründe bulunan TÜM tedarik işaretleri kaldırılacak${hasDeliveries ? " ve ilgili teslimat kayıtları iptal edilecek" : ""}. Emin misiniz?\n\nKaldırdığınız zaman tedarik edilmiş olarak gösterilen ürünlerin tedarik durumunu sıfırlayacaktır.`,
      async () => {
        try {
          const results = await Promise.all(assigned.map((p) => api.post("/procurement/unassign-product", { sku: p.sku, variant: p.variant || null, reset_deliveries: true })));
          const removed = results.reduce((a, r) => a + (r.data.removed || 0), 0);
          toast.success(`${removed} tedarik işareti kaldırıldı`);
          setCatAssign((c) => (c ? { ...c, products: c.products.map((x) => ({ ...x, procuredQty: 0, directQty: 0, supplierBreakdown: [] })) } : c));
          setRefreshTick((t) => t + 1);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Atamalar kaldırılamadı");
        }
      }
    );
  };

  const removeSupplierAlloc = async (b) => {
    try {
      await api.post("/procurement/unassign-supplier", { sku: selected.sku, variant: selected.variant || null, supplier_id: b.supplierId });
      toast.success(`${b.supplier} ataması kaldırıldı`);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Atama kaldırılamadı");
    }
  };

  useEffect(() => {
    if (canAssign) api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  }, [canAssign]);

  const applyProcureState = (target, lines, supplierName) => {
    const lineKeys = new Set(lines.map((o) => `${o.ref}|${o.lineIdx}`));
    const upd = (p) =>
      p.sku !== selected.sku || (p.variant || "") !== (selected.variant || "")
        ? p
        : (() => {
            const orderers = p.orderers.map((x) =>
              lineKeys.has(`${x.ref}|${x.lineIdx}`) ? { ...x, procured: target, assignedQty: target ? x.qty : 0, supplier: target ? supplierName : null } : x
            );
            const procuredQty = orderers
              .filter((x) => x.status !== "CANCELLED" || status === "CANCELLED")
              .reduce((a, x) => a + (x.procured ? x.qty || 0 : x.assignedQty || 0), 0);
            return { ...p, orderers, procuredQty };
          })();
    setGroups((gs) => gs.map((g) => ({ ...g, products: g.products.map(upd) })));
    setSelected((p) => (p ? upd(p) : p));
  };

  const toggleAllProcured = async () => {
    const target = !selected.orderers.every((o) => o.procured);
    if (target) {
      setProcureSupplier("");
      setDirectBuy(false);
      setFreeVendor("");
      setProcureDialog({ mode: "all" });
      return;
    }
    const resetDeliveries = (selected.deliveredQty || 0) > 0;
    askConfirm("Tedarik işaretlerini kaldır", `Tedarik işaretlerini kaldırmak istediğinize emin misiniz?\n\nKaldırdığınız zaman tedarik edilmiş olarak gösterilen ürünlerin tedarik durumunu sıfırlayacaktır.${resetDeliveries ? " Bu işlem üründeki TÜM teslimat kayıtlarını da iptal edecek." : ""}`, async () => {
      try {
        await api.post("/procurement/bulk", {
          sku: selected.sku, name: selected.name, procured: false, reset_deliveries: resetDeliveries,
          items: selected.orderers.map((o) => ({ ref: o.ref, variant: o.variant, detail: o.detail, lineIdx: o.lineIdx, qty: o.qty, organisation: o.organisation })),
        });
        applyProcureState(false, selected.orderers, null);
        setRefreshTick((t) => t + 1);
        toast.success(resetDeliveries ? "Tüm işaretler ve teslimat kayıtları kaldırıldı" : "Tüm işaretler kaldırıldı");
      } catch {
        toast.error("Toplu işaretleme kaydedilemedi");
      }
    });
  };

  const toggleProcured = async (o) => {
    if (!o.procured) {
      setProcureSupplier("");
      setDirectBuy(false);
      setFreeVendor("");
      setProcureDialog({ mode: "single", line: o });
      return;
    }
    const willRemain = Math.max(0, (selected.procuredQty || 0) - (o.qty || 0));
    const resetDeliveries = (selected.deliveredQty || 0) > willRemain;
    askConfirm("Tedarik işaretini kaldır", `Bu satırın tedarik işaretini kaldırmak istediğinize emin misiniz?\n\nKaldırdığınız zaman tedarik edilmiş olarak gösterilen ürünün tedarik durumunu sıfırlayacaktır.${resetDeliveries ? " Teslim edilen miktar kalan atamayı aşacağı için üründeki TÜM teslimat kayıtları da iptal edilecek." : ""}`, async () => {
      try {
        await api.post("/procurement/toggle", {
          sku: selected.sku, ref: o.ref, variant: o.variant, detail: o.detail,
          lineIdx: o.lineIdx, qty: o.qty, organisation: o.organisation, reset_deliveries: resetDeliveries,
        });
        applyProcureState(false, [o], null);
        setRefreshTick((t) => t + 1);
        toast.success(resetDeliveries ? "İşaret ve teslimat kayıtları kaldırıldı" : "İşaret kaldırıldı");
      } catch {
        toast.error("İşaretleme kaydedilemedi");
      }
    });
  };

  const confirmProcure = async () => {
    const supplierName = suppliers.find((s) => s.id === procureSupplier)?.name || (directBuy ? freeVendor.trim() || "Doğrudan Satınalma" : "");
    const extra = { supplier_id: procureSupplier || undefined, direct: directBuy, free_vendor: directBuy && !procureSupplier ? freeVendor.trim() || undefined : undefined };
    const successLabel = directBuy ? `${supplierName} — doğrudan satınalma olarak işaretlendi` : null;
    try {
      if (procureDialog.mode === "all") {
        const r = await api.post("/procurement/bulk", {
          sku: selected.sku, name: selected.name, procured: true, ...extra,
          items: selected.orderers.map((o) => ({ ref: o.ref, variant: o.variant, detail: o.detail, lineIdx: o.lineIdx, qty: o.qty, organisation: o.organisation })),
        });
        applyProcureState(true, selected.orderers, supplierName);
        toast.success(successLabel || `Tüm satırlar ${supplierName} tedarikçisine işaretlendi${r.data.notified ? " · Tedarikçiye e-posta gönderildi" : ""}`);
      } else {
        const o = procureDialog.line;
        const r = await api.post("/procurement/toggle", {
          sku: selected.sku, name: selected.name, ref: o.ref, variant: o.variant, detail: o.detail,
          lineIdx: o.lineIdx, qty: o.qty, organisation: o.organisation, ...extra,
        });
        applyProcureState(true, [o], supplierName);
        toast.success(successLabel || `${supplierName} tedarikçisine sipariş verildi olarak işaretlendi${r.data.notified ? " · Tedarikçiye e-posta gönderildi" : ""}`);
      }
      setProcureDialog(null);
      setRefreshTick((t) => t + 1);
    } catch {
      toast.error("İşaretleme kaydedilemedi");
    }
  };

  useEffect(() => {
    setLoading(true);
    api
      .get("/orders/by-product", { params: { space_type: spaceType || undefined, status: status !== "__all__" ? status : undefined, currency: currency !== "__all__" ? currency : undefined, organisation: organisation !== "__all__" ? organisation : undefined, zone: zone || undefined, country: country !== "__all__" ? country : undefined } })
      .then((r) => {
        setGroups(r.data.data);
        setSelected((prev) => {
          if (!prev) return prev;
          for (const g of r.data.data) {
            const f = g.products.find((p) => p.sku === prev.sku && (p.variant || "") === (prev.variant || ""));
            if (f) return f;
          }
          return prev;
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [spaceType, status, currency, organisation, zone, country, refreshTick]);

  const categories = useMemo(() => groups.map((g) => g.category), [groups]);

  const subCategories = useMemo(() => {
    const base = category.length === 0 ? groups : groups.filter((g) => category.includes(g.category));
    const set = new Set();
    base.forEach((g) => g.products.forEach((p) => p.subCategory && set.add(p.subCategory)));
    return [...set].sort((a, b) => a.localeCompare(b, "tr"));
  }, [groups, category]);

  useEffect(() => {
    setSubCategory((s) => s.filter((x) => subCategories.includes(x)));
  }, [subCategories]);

  useEffect(() => {
    setCategory((c) => c.filter((x) => categories.includes(x)));
  }, [categories]);

  const filtered = useMemo(() => {
    let base = category.length === 0 ? groups : groups.filter((g) => category.includes(g.category));
    if (subCategory.length > 0) {
      base = base
        .map((g) => ({ ...g, products: g.products.filter((p) => subCategory.includes(p.subCategory)) }))
        .filter((g) => g.products.length > 0);
    }
    if (kind !== "__all__") {
      base = base
        .map((g) => ({ ...g, products: g.products.filter((p) => p.kind === kind) }))
        .filter((g) => g.products.length > 0);
    }
    if (search) {
      const s = search.trim().toLocaleLowerCase("tr");
      const codeNames = Object.fromEntries(countries.map((c) => [c.code, c.name]));
      const refCountry = (ref) => {
        const parts = (ref || "").split("-");
        const code = parts.length > 2 && /^[A-Z]{3}$/.test(parts[2]) ? parts[2] : "";
        return codeNames[code] || "";
      };
      const hit = (p) =>
        (p.name || "").toLocaleLowerCase("tr").includes(s) ||
        (p.sku || "").toLowerCase().includes(s) ||
        (p.orderers || []).some((o) =>
          (o.plot || "").toLowerCase().includes(s) ||
          (o.space || "").toLocaleLowerCase("tr").includes(s) ||
          refCountry(o.ref).toLocaleLowerCase("tr").includes(s) ||
          (orgCountries[o.organisation] || "").toLocaleLowerCase("tr").includes(s));
      base = base
        .map((g) => ({ ...g, products: g.products.filter(hit) }))
        .filter((g) => g.products.length > 0);
    }
    return base.map((g) => ({ ...g, totalQty: Math.round(g.products.reduce((a, p) => a + (p.qty || 0), 0) * 100) / 100 }));
  }, [groups, search, category, subCategory, kind, orgCountries, countries]);

  const productCount = filtered.reduce((a, g) => a + g.products.length, 0);
  const hasFilters = search || spaceType || zone || kind !== "__all__" || category.length > 0 || subCategory.length > 0 || status !== "__all__" || currency !== "__all__" || organisation !== "__all__" || country !== "__all__";
  const resetFilters = () => {
    setSearch("");
    setSpaceType("");
    setKind("__all__");
    setZone("");
    setCategory([]);
    setSubCategory([]);
    setCountry("__all__");
    setStatus("__all__");
    setCurrency("__all__");
    setOrganisation("__all__");
  };
  const [exporting, setExporting] = useState(false);
  const [view, setView] = useState("list");

  const filterParams = {
    space_type: spaceType || undefined,
    category: category.length ? category.join("||") : undefined,
    sub_category: subCategory.length ? subCategory.join("||") : undefined,
    search: search || undefined,
    status: status !== "__all__" ? status : undefined,
    currency: currency !== "__all__" ? currency : undefined,
    organisation: organisation !== "__all__" ? organisation : undefined,
    zone: zone || undefined,
    country: country !== "__all__" ? country : undefined,
    kind: kind !== "__all__" ? kind : undefined,
  };

  const [deliveredMarks, setDeliveredMarks] = useState({});
  const [onlyDelivered, setOnlyDelivered] = useState(false);
  const canMarkDelivered = (isAdmin || user?.role === "malkabul") && organisation !== "__all__";

  useEffect(() => {
    setOnlyDelivered(false);
    if (organisation === "__all__") {
      setDeliveredMarks({});
      return;
    }
    api.get("/delivered-marks", { params: { organisation } }).then((r) => {
      const m = {};
      r.data.data.forEach((x) => { m[`${x.sku}|${x.variant || ""}`] = x.delivered_at; });
      setDeliveredMarks(m);
    }).catch(() => {});
  }, [organisation]);

  const toggleDelivered = async (items, delivered) => {
    try {
      const r = await api.post("/delivered-marks/toggle", {
        organisation,
        items: items.map((p) => ({ sku: p.sku || p.name, variant: p.variant || null })),
        delivered,
      });
      setDeliveredMarks((prev) => {
        const m = { ...prev };
        items.forEach((p) => {
          const k = dKey(p);
          if (delivered) m[k] = r.data.delivered_at;
          else delete m[k];
        });
        return m;
      });
      toast.success(delivered ? "Teslim edildi olarak işaretlendi" : "Teslim işareti kaldırıldı");
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşaretlenemedi");
    }
  };
  const onlyDeliveredParam = onlyDelivered && organisation !== "__all__" ? 1 : undefined;

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/orders/by-product/export", {
        params: filterParams,
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cop31_urun_bazinda_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi");
    } catch {
      toast.error("Dışa aktarma başarısız oldu");
    } finally {
      setExporting(false);
    }
  };

  const openCatAssign = (g) => {
    if (suppliers.length === 0) {
      toast.error("Önce Kullanıcılar sayfasından tedarikçi ekleyin");
      return;
    }
    const checked = {};
    g.products.forEach((p) => { checked[`${p.sku}|${p.variant || ""}`] = true; });
    setCatChecked(checked);
    setCatSupplier("");
    setCatAssign(g);
  };

  const confirmCatAssign = async () => {
    const keys = Object.entries(catChecked).filter(([, v]) => v).map(([k]) => k);
    const supplierName = suppliers.find((s) => s.id === catSupplier)?.name;
    try {
      const r = await api.post("/procurement/assign-category", {
        category: catAssign.category, supplier_id: catSupplier, keys,
        space_type: spaceType || "", status: status !== "__all__" ? status : "",
        currency: currency !== "__all__" ? currency : "", organisation: organisation !== "__all__" ? organisation : "",
        zone: zone || "",
      });
      toast.success(
        `${r.data.marked} satır ${supplierName} tedarikçisine atandı` +
        (r.data.already ? ` · ${r.data.already} satır zaten atanmıştı (dokunulmadı)` : "") +
        (r.data.notified ? " · Tedarikçiye e-posta gönderildi" : "")
      );
      setCatAssign(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Atama yapılamadı");
    }
  };

  const categoryExcel = async (cat) => {
    try {
      const res = await api.get("/orders/by-product/export", {
        params: {
          space_type: spaceType || undefined,
          category: cat,
          sub_category: subCategory.length ? subCategory.join("||") : undefined,
          search: search || undefined,
          status: status !== "__all__" ? status : undefined,
          currency: currency !== "__all__" ? currency : undefined,
          organisation: organisation !== "__all__" ? organisation : undefined,
          zone: zone || undefined,
          country: country !== "__all__" ? country : undefined,
        },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cop31_kategori_${cat.replace(/\s+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel dosyası indirildi");
    } catch {
      toast.error("Dışa aktarma başarısız oldu");
    }
  };

  return (
    <div className="space-y-4" data-testid="orders-by-product-page">
      <div className="flex flex-wrap items-center gap-3 border border-stone-200 bg-white p-4">
        <div className="flex overflow-hidden border border-stone-300" data-testid="space-type-filter">
          {[["", "Tümü"], ["delegation", "Ofisler"], ["pavilion", "Pavilyonlar"], ["stand", "Stantlar"]].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setSpaceType(val)}
              data-testid={`space-type-${val || "all"}`}
              className={`px-3.5 py-2 text-xs font-medium transition-colors duration-150 ${
                spaceType === val ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex overflow-hidden border border-stone-300" data-testid="kind-filter">
          {[["__all__", "Tümü"], ["product", "Ürünler"], ["service", "Servisler"], ["depo_disi", "Depo Dışı"]].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setKind(val)}
              data-testid={`kind-${val === "__all__" ? "all" : val}`}
              className={`px-3.5 py-2 text-xs font-medium transition-colors duration-150 ${
                kind === val ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {zones.length > 0 && (
          <div className="flex overflow-hidden border border-stone-300" data-testid="zone-filter">
            {[["", "Tüm Zone'lar"], ...zones.map((z) => [z, `${z.charAt(0).toUpperCase() + z.slice(1)} Zone`])].map(([val, label]) => (
              <button
                key={val || "all"}
                onClick={() => setZone(val)}
                data-testid={`zone-${val || "all"}`}
                className={`px-3.5 py-2 text-xs font-medium transition-colors duration-150 ${
                  zone === val ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <SearchInput value={search} onChange={setSearch} placeholder="Ürün, SKU, stand no, ülke veya alan ara..." testId="by-product-search-input" className="min-w-[220px] flex-1" />
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger className="w-[180px] rounded-sm border-stone-300" data-testid="by-product-country-select">
            <SelectValue placeholder="Ülke" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="__all__">{countries.length === 1 ? countries[0].name : `Tüm Ülkeler (${countries.length})`}</SelectItem>
            {countries.map((c) => (
              <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <OrgCombobox value={organisation} onChange={setOrganisation} organisations={organisations} orgCountries={orgCountries} />
        <MultiSelect options={categories} values={category} onChange={setCategory} allLabel="Tüm Kategoriler" widthClass="w-[230px]" testId="by-product-category-select" />
        <MultiSelect options={subCategories} values={subCategory} onChange={setSubCategory} allLabel="Tüm Alt Kategoriler" widthClass="w-[200px]" testId="by-product-subcategory-select" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px] rounded-sm border-stone-300" data-testid="by-product-status-select">
            <SelectValue placeholder="Ödeme Durumu" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Tüm Durumlar</SelectItem>
            {statuses.map((s) => (
              <SelectItem key={s} value={s}>{statusLabel(s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={currency} onValueChange={setCurrency}>
          <SelectTrigger className="w-[150px] rounded-sm border-stone-300" data-testid="by-product-currency-select">
            <SelectValue placeholder="Para Birimi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Para Birimleri</SelectItem>
            {currencies.map((c) => (
              <SelectItem key={c} value={c}>{c === "USD" ? "USD ($)" : c === "EUR" ? "EUR (€)" : c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="by-product-filters-reset" className="rounded-sm text-xs text-stone-500 hover:bg-stone-100">
            <X size={13} className="mr-1" /> Filtreleri Temizle
          </Button>
        )}
        <p className="text-xs text-stone-500" data-testid="by-product-count">{productCount} ürün sipariş edildi</p>
        {!isLimited && (
        <Button
          variant="outline"
          size="sm"
          onClick={exportExcel}
          disabled={exporting || productCount === 0}
          data-testid="by-product-export-button"
          className="h-8 rounded-sm border-stone-300 text-xs hover:bg-stone-100 active:scale-95"
        >
          <Download size={13} className="mr-1.5" />
          {exporting ? "Hazırlanıyor..." : "Excel'e Aktar"}
        </Button>
        )}
        {organisation !== "__all__" && !isViewer && (
          <label className="flex cursor-pointer items-center gap-1.5 border border-green-600/40 bg-green-50 px-2 py-2 text-[11px] font-semibold text-green-800" data-testid="only-delivered-toggle" title="Aktifken Yazdır / PDF İndir / Resimli Rapor sadece teslim edildi işaretli ürünleri içerir">
            <Checkbox checked={onlyDelivered} onCheckedChange={(v) => setOnlyDelivered(!!v)} data-testid="only-delivered-checkbox" className="h-3.5 w-3.5 border-green-600 data-[state=checked]:border-green-700 data-[state=checked]:bg-green-700" />
            Sadece işaretlileri PDF'e al
          </label>
        )}
        {!isLimited && productCount > 0 && (
          <div className="flex items-center gap-2 border border-stone-300 bg-stone-50 py-1 pl-2 pr-1" data-testid="visual-report-actions">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-stone-500">Resimli Rapor</span>
            <DetailActions
              compact
              pdfPath="/orders/by-product/visual-pdf"
              pdfParams={{ ...filterParams, only_delivered: onlyDeliveredParam }}
              showDeliveredFilter={organisation !== "__all__"}
              emailPath="/orders/by-product/visual-email"
              emailPayload={filterParams}
              filename={`cop31_resimli_rapor_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`}
              testPrefix="visual"
              historyScope="byproduct-visual"
              showMode={false}
            />
          </div>
        )}
        <div className="flex overflow-hidden border border-stone-300" data-testid="view-toggle">
          <button
            onClick={() => setView("list")}
            data-testid="view-toggle-list"
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors duration-150 ${view === "list" ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
          >
            <List size={13} /> Liste
          </button>
          <button
            onClick={() => setView("grid")}
            data-testid="view-toggle-grid"
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors duration-150 ${view === "grid" ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
          >
            <LayoutGrid size={13} /> Grid
          </button>
          <button
            onClick={() => setView("calendar")}
            data-testid="view-toggle-calendar"
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors duration-150 ${view === "calendar" ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
          >
            <CalendarDays size={13} /> Takvim
          </button>
        </div>
      </div>

      <StatusKpis spaceType={spaceType} organisation={organisation} currency={currency} zone={zone} status={status} search={search} kind={kind} category={category} subCategory={subCategory} country={country} hideMoney={isViewer} onToggle={(st) => setStatus((cur) => (cur === st ? "__all__" : st))} />

      {organisation !== "__all__" && (
        <CustomerKpiPanel
          organisation={organisation}
          status={status}
          spaceType={spaceType}
          kind={kind}
          country={country}
          hideMoney={isViewer}
          onlyDelivered={!!onlyDeliveredParam}
          onStatusToggle={(st) => setStatus((cur) => (cur === st ? "__all__" : st))}
        />
      )}

      <div className="border border-stone-200 bg-white">
        {view === "calendar" ? (
          <ServiceCalendar spaceType={spaceType} status={status} currency={currency} organisation={organisation} zone={zone} />
        ) : loading ? (
          <div className="flex h-40 items-center justify-center" data-testid="by-product-loading">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#1C4E35] border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="p-8 text-center text-sm text-stone-500" data-testid="by-product-empty">Sipariş edilen ürün bulunamadı.</p>
        ) : view === "grid" ? (
          <div className="space-y-6 p-4" data-testid="by-product-grid">
            {filtered.map((g) => (
              <div key={g.category} data-testid={`by-product-grid-group-${g.category}`}>
                <div className="mb-2 flex flex-wrap items-baseline gap-2">
                  <h3 className="font-heading text-xs font-bold uppercase tracking-[0.15em] text-[#1C4E35]">{g.category}</h3>
                  <span className="text-[11px] text-stone-500">{g.products.length} ürün · {g.totalQty} adet</span>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {g.products.map((p) => (
                    <button
                      type="button"
                      key={`${p.sku || p.name}|${p.variant || ""}`}
                      onClick={() => setSelected(p)}
                      data-testid={`by-product-card-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                      className="flex flex-col border border-stone-200 bg-white p-3 text-left transition-all duration-150 hover:border-[#1C4E35] hover:shadow-sm active:scale-[0.99]"
                    >
                      {p.image && (
                        <img
                          src={mediaUrl(p.image)}
                          alt={p.name}
                          loading="lazy"
                          style={beigeStyle(p.variant)}
                          onClick={(e) => { e.stopPropagation(); setLightbox(p); }}
                          data-testid={`grid-image-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                          className="mb-2 h-28 w-full cursor-zoom-in border border-stone-100 bg-white object-contain transition-opacity hover:opacity-80"
                        />
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium leading-snug">{p.name}</p>
                        <span className="flex items-center gap-1">
                          {p.variant && <VariantBadge variant={p.variant} />}
                          {p.manualQty > 0 && <span className="border border-violet-300 bg-violet-50 px-1 py-px text-[9px] font-bold text-violet-700" title={`Manuel ekleme içerir (${p.manualQty} adet)`}>M</span>}
                        </span>
                      </div>
                      {deliveredMarks[dKey(p)] && (
                        <span className="mt-1 inline-block self-start border border-green-600 bg-green-50 px-1.5 py-px text-[9px] font-bold text-green-700">
                          Teslim Edildi · {format(new Date(deliveredMarks[dKey(p)]), "dd.MM.yyyy")}
                        </span>
                      )}
                      <p className="mt-0.5 text-[11px] text-stone-400">
                        {p.sku}
                        <span className="ml-1 text-stone-500">· {g.category}{p.subCategory ? ` · ${p.subCategory}` : ""}</span>
                      </p>
                      <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 pt-2 text-[11px] text-stone-600">
                        <span><strong className="text-[#0A0D0B]">{p.qty}</strong> adet</span>
                        <span><strong className="text-[#0A0D0B]">{p.orderCount}</strong> sipariş</span>
                        {!isViewer && <span className="ml-auto text-sm font-semibold text-[#0A0D0B]">{fmtMoney(p.total, p.currency)}</span>}
                      </div>
                      {isStaff && (
                        <span className={`mt-1.5 inline-block self-start border px-1.5 py-0.5 text-[10px] font-medium ${procureChip(p).cls}`}>
                          {procureChip(p).text}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Table data-testid="by-product-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Ürün</TableHead>
                <TableHead className="text-right text-xs">Toplam Miktar</TableHead>
                <TableHead className="text-right text-xs">Sipariş Sayısı</TableHead>
                {!isViewer && <TableHead className="text-right text-xs">Toplam Tutar</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((g) => (
                <Fragment key={g.category}>
                  <TableRow
                    onClick={() => setExpandedCats((c) => ({ ...c, [g.category]: !c[g.category] }))}
                    className="cursor-pointer select-none hover:bg-stone-200/60"
                    data-testid={`by-product-group-${g.category}`}
                  >
                    <TableCell colSpan={isViewer ? 3 : 4} className="border-y border-stone-200 bg-stone-100 py-2">
                      <span className="flex items-center gap-2">
                        <ChevronDown size={14} className={`text-stone-500 transition-transform duration-200 ${isCatOpen(g.category) ? "" : "-rotate-90"}`} />
                        {canMarkDelivered && (
                          <span onClick={(e) => e.stopPropagation()} title="Tüm kategoriyi teslim edildi işaretle">
                            <Checkbox
                              checked={g.products.every((p) => deliveredMarks[dKey(p)]) ? true : g.products.some((p) => deliveredMarks[dKey(p)]) ? "indeterminate" : false}
                              onCheckedChange={() => toggleDelivered(g.products, !g.products.every((p) => deliveredMarks[dKey(p)]))}
                              data-testid={`delivered-cat-checkbox-${g.category.replace(/\s+/g, "_")}`}
                              className="h-4 w-4 border-green-600 data-[state=checked]:border-green-700 data-[state=checked]:bg-green-700"
                            />
                          </span>
                        )}
                        <span className="font-heading text-xs font-bold uppercase tracking-[0.15em] text-[#1C4E35]">{g.category}</span>
                        <span className="text-[11px] text-stone-500">{g.products.length} ürün · {g.totalQty} adet</span>
                        <span className="ml-auto flex items-center gap-1 pr-1" onClick={(e) => e.stopPropagation()} data-testid={`category-actions-${g.category.replace(/\s+/g, "_")}`}>
                          {canAssign && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openCatAssign(g)}
                              title="Kategoriyi tedarikçiye ata"
                              data-testid={`category-assign-${g.category.replace(/\s+/g, "_")}`}
                              className="h-7 w-7 rounded-sm border-stone-300 bg-white p-0 hover:bg-stone-100 active:scale-95"
                            >
                              <Truck size={13} />
                            </Button>
                          )}
                          {!isLimited && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => categoryExcel(g.category)}
                            title="Kategoriyi Excel'e aktar"
                            data-testid={`category-excel-${g.category.replace(/\s+/g, "_")}`}
                            className="h-7 w-7 rounded-sm border-stone-300 bg-white p-0 hover:bg-stone-100 active:scale-95"
                          >
                            <FileSpreadsheet size={13} />
                          </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCatHistory((c) => (c === g.category ? null : g.category))}
                            title="E-posta gönderim geçmişi"
                            data-testid={`category-history-${g.category.replace(/\s+/g, "_")}`}
                            className={`h-7 w-7 rounded-sm border-stone-300 p-0 hover:bg-stone-100 active:scale-95 ${catHistory === g.category ? "bg-stone-200" : "bg-white"}`}
                          >
                            <History size={13} />
                          </Button>
                          )}
                          {!isLimited && (
                          <DetailActions
                            compact
                            pdfPath="/orders/by-category/pdf"
                            pdfParams={{ category: g.category, sub_category: subCategory.length ? subCategory.join("||") : undefined, search: search || undefined, space_type: spaceType || undefined, status: status !== "__all__" ? status : undefined, currency: currency !== "__all__" ? currency : undefined, organisation: organisation !== "__all__" ? organisation : undefined, zone: zone || undefined, country: country !== "__all__" ? country : undefined, kind: kind !== "__all__" ? kind : undefined, only_delivered: onlyDeliveredParam }}
                            showDeliveredFilter={organisation !== "__all__"}
                            emailPath="/orders/by-category/email"
                            emailPayload={{ category: g.category, sub_category: subCategory.length ? subCategory.join("||") : "", search: search || "", space_type: spaceType || "", status: status !== "__all__" ? status : "", currency: currency !== "__all__" ? currency : "", organisation: organisation !== "__all__" ? organisation : "", zone: zone || "", country: country !== "__all__" ? country : "", kind: kind !== "__all__" ? kind : "" }}
                            filename={`cop31_kategori_${g.category.replace(/\s+/g, "_")}.pdf`}
                            testPrefix={`category-${g.category.replace(/\s+/g, "_")}`}
                            historyScope={`category|${g.category}`}
                          />
                          )}
                        </span>
                      </span>
                    </TableCell>
                  </TableRow>
                  {catHistory === g.category && (
                    <TableRow data-testid={`category-history-row-${g.category.replace(/\s+/g, "_")}`}>
                      <TableCell colSpan={isViewer ? 3 : 4} className="bg-stone-50 py-2">
                        <EmailHistory scope={`category|${g.category}`} testPrefix={`category-${g.category.replace(/\s+/g, "_")}`} />
                      </TableCell>
                    </TableRow>
                  )}
                  {isCatOpen(g.category) &&
                    g.products.map((p) => (
                      <TableRow
                        key={`${p.sku || p.name}|${p.variant || ""}`}
                        onClick={() => setSelected(p)}
                        className="cursor-pointer transition-colors duration-150 hover:bg-stone-50"
                        data-testid={`by-product-row-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                      >
                        <TableCell className="py-2">
                          <span className="flex items-start gap-2.5">
                          {canMarkDelivered && (
                            <span className="mt-3" onClick={(e) => e.stopPropagation()} title="Teslim edildi işaretle">
                              <Checkbox
                                checked={!!deliveredMarks[dKey(p)]}
                                onCheckedChange={(v) => toggleDelivered([p], !!v)}
                                data-testid={`delivered-checkbox-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                                className="h-4 w-4 border-green-600 data-[state=checked]:border-green-700 data-[state=checked]:bg-green-700"
                              />
                            </span>
                          )}
                          {p.image && (
                            <img
                              src={mediaUrl(p.image)}
                              alt={p.name}
                              loading="lazy"
                              style={beigeStyle(p.variant)}
                              onClick={(e) => { e.stopPropagation(); setLightbox(p); }}
                              data-testid={`row-image-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                              className="mt-0.5 h-11 w-11 shrink-0 cursor-zoom-in border border-stone-200 bg-white object-contain transition-transform hover:scale-105"
                            />
                          )}
                          <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <p className="text-sm font-medium">{p.name}</p>
                            {p.variant && <VariantBadge variant={p.variant} />}
                            {p.manualQty > 0 && <span className="border border-violet-300 bg-violet-50 px-1 py-px text-[9px] font-bold text-violet-700" title={`Manuel ekleme içerir (${p.manualQty} adet)`} data-testid={`manual-badge-${p.sku}`}>M</span>}
                            {deliveredMarks[dKey(p)] && (
                              <span className="border border-green-600 bg-green-50 px-1.5 py-px text-[9px] font-bold text-green-700" data-testid={`delivered-badge-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}>
                                Teslim Edildi · {format(new Date(deliveredMarks[dKey(p)]), "dd.MM.yyyy")}
                              </span>
                            )}
                          </span>
                          <p className="text-[11px] text-stone-400">
                            {p.sku}
                            <span className="ml-1.5 text-stone-500" data-testid={`row-category-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}>
                              · {g.category}{p.subCategory ? ` · ${p.subCategory}` : ""}
                            </span>
                          </p>
                          {isStaff && (
                            <span
                              data-testid={`procured-chip-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                              className={`mt-1 inline-block border px-1.5 py-0.5 text-[10px] font-medium ${
                                p.qty > 0 && (p.procuredQty || 0) >= p.qty
                                  ? (p.procuredQty || 0) - (p.directQty || 0) <= 0 && (p.directQty || 0) > 0
                                    ? "border-sky-400 bg-sky-50 text-sky-700"
                                    : "border-green-500 bg-green-50 text-green-700"
                                  : (p.procuredQty || 0) > 0
                                  ? "border-amber-400 bg-amber-50 text-amber-800"
                                  : "border-stone-300 bg-stone-50 text-stone-500"
                              }`}
                            >
                              {(() => {
                                const total = p.procuredQty || 0;
                                const d = p.directQty || 0;
                                const s = Math.max(0, total - d);
                                const rem = p.qty - total;
                                if (rem < 0) return `Tedarikçi: ${total}/${p.qty} · Fazla +${-rem}`;
                                if (rem > 0) return `Tedarikçi: ${s}/${p.qty} · Kalan ${rem}`;
                                if (d === 0) return `Tedarikçi Atandı: ${s}/${p.qty} ✓`;
                                return s > 0 ? `Tedarikçi Atandı: ${s}/${p.qty} ✓` : `Doğrudan Satınalma: ${total}/${p.qty} ✓`;
                              })()}
                            </span>
                          )}
                          {isStaff && (p.directQty || 0) > 0 && !(p.qty - (p.procuredQty || 0) <= 0 && (p.procuredQty || 0) - (p.directQty || 0) <= 0) && (
                            <span
                              data-testid={`direct-chip-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                              className="ml-1 mt-1 inline-block border border-sky-400 bg-sky-50 px-1.5 py-0.5 text-[10px] font-medium text-sky-700"
                            >
                              Doğrudan Satınalma: {p.directQty}
                            </span>
                          )}
                          {isStaff && ((p.procuredQty || 0) > 0 || (p.deliveredQty || 0) > 0) && (
                            <span
                              data-testid={`delivered-chip-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                              className={`ml-1 mt-1 inline-block border px-1.5 py-0.5 text-[10px] font-medium ${
                                (p.deliveredQty || 0) >= p.qty
                                  ? "border-green-500 bg-green-50 text-green-700"
                                  : "border-amber-400 bg-amber-50 text-amber-800"
                              }`}
                            >
                              Teslim: {p.deliveredQty || 0}/{p.qty}
                            </span>
                          )}
                          {isStaff && (p.acceptedQty || 0) > 0 && (
                            <span
                              data-testid={`accepted-chip-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                              className={`ml-1 mt-1 inline-block border px-1.5 py-0.5 text-[10px] font-medium ${
                                (p.acceptedQty || 0) === p.qty
                                  ? "border-green-500 bg-green-50 text-green-700"
                                  : (p.acceptedQty || 0) < p.qty
                                  ? "border-red-400 bg-red-50 text-red-700"
                                  : "border-amber-400 bg-amber-50 text-amber-800"
                              }`}
                            >
                              Mal Kabul: {p.acceptedQty}/{p.qty}
                              {p.acceptedQty === p.qty ? " ✓" : p.acceptedQty < p.qty ? ` · Eksik ${p.qty - p.acceptedQty}` : ` · Fazla +${p.acceptedQty - p.qty}`}
                            </span>
                          )}
                          </span>
                          </span>
                        </TableCell>
                        <TableCell className="py-2 text-right">
                          <span className="font-heading inline-block min-w-[2.5rem] border border-[#1C4E35]/30 bg-[#1C4E35]/10 px-2 py-0.5 text-sm font-bold text-[#1C4E35]">
                            {p.qty} <span className="text-xs font-medium">{unitLabel(p.unit)}</span>
                          </span>
                        </TableCell>
                        <TableCell className="py-2 text-right text-sm">{p.orderCount}</TableCell>
                        {!isViewer && <TableCell className="py-2 text-right text-sm font-medium">{fmtMoney(p.total, p.currency)}</TableCell>}
                      </TableRow>
                    ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && closeProductModal()}>
        <DialogContent aria-describedby={undefined} className="max-h-[88vh] w-[92vw] !max-w-4xl overflow-y-auto rounded-sm bg-white" data-testid="by-product-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2 text-lg font-bold tracking-tight">
                  <PackageSearch size={18} className="text-[#1C4E35]" />
                  {selected.name}
                  {selected.manualQty > 0 && <span className="border border-violet-300 bg-violet-50 px-1.5 py-px text-[10px] font-bold text-violet-700" title={`Manuel ekleme içerir (${selected.manualQty} adet)`} data-testid="dialog-manual-badge">M</span>}
                  {selected.variant && <VariantBadge variant={selected.variant} />}
                </DialogTitle>
              </DialogHeader>
              <p className="text-[11px] uppercase tracking-wider text-stone-400">
                {selected.sku} · {selected.category}{selected.subCategory ? ` · ${selected.subCategory}` : ""}
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="product-detail-kpis">
                <Kpi icon={Boxes} label="Toplam Miktar" value={`${selected.qty} ${unitLabel(selected.unit)}`} testId="product-kpi-qty" />
                <Kpi icon={ShoppingCart} label="Sipariş Sayısı" value={selected.orderCount} testId="product-kpi-orders" />
                <Kpi icon={Building2} label="Kurum" value={new Set((status === "CANCELLED" ? selected.orderers : selected.orderers.filter((x) => x.status !== "CANCELLED")).map((o) => o.organisation)).size} testId="product-kpi-orgs" />
                {!isViewer && <Kpi icon={Banknote} label="Toplam Tutar" value={fmtMoney(selected.total, selected.currency)} testId="product-kpi-total" />}
              </div>

              {selected.image && (
                <img
                  src={mediaUrl(selected.image)}
                  alt={selected.name}
                  data-testid="product-modal-image"
                  style={beigeStyle(selected.variant)}
                  onClick={() => setLightbox(selected)}
                  className="h-44 w-full cursor-zoom-in border border-stone-200 bg-white object-contain transition-opacity hover:opacity-80"
                />
              )}
              {isStaff && (
                <div className="border border-stone-200 bg-stone-50 px-3 py-2" data-testid="procurement-progress">
                  <div className="flex flex-wrap items-center justify-between gap-x-2 text-xs">
                    <span className="flex items-center gap-2 font-medium text-stone-600">
                      Tedarik Durumu
                      {canAssign && (selected.procuredQty || 0) > 0 && (
                        <button
                          type="button"
                          onClick={removeAllMarks}
                          title="Bu üründeki tüm tedarik işaretlerini tek seferde kaldır"
                          data-testid="remove-all-marks"
                          className="border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-600 transition-colors hover:bg-red-100 active:scale-95"
                        >
                          Tümünü Kaldır
                        </button>
                      )}
                    </span>
                    <span className={`font-heading font-black ${selected.qty > 0 && (selected.procuredQty || 0) >= selected.qty ? "text-green-700" : "text-[#1C4E35]"}`}>
                      {selected.procuredQty || 0}/{selected.qty} {unitLabel(selected.unit)}
                      {(() => {
                        const total = selected.procuredQty || 0;
                        const d = selected.directQty || 0;
                        const rem = selected.qty - total;
                        if (rem < 0) return ` · Sipariş Fazlası +${-rem}`;
                        if (rem > 0) return ` · Kalan ${rem}`;
                        if (d > 0 && total - d <= 0) return " · Doğrudan Satınalma ✓";
                        if (d > 0) return " · Atama Tamam ✓";
                        return " · Tedarikçi Atandı ✓";
                      })()}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full bg-stone-200">
                    <div
                      className={`h-1.5 transition-all duration-300 ${selected.qty > 0 && (selected.procuredQty || 0) >= selected.qty ? "bg-green-600" : "bg-[#1C4E35]"}`}
                      style={{ width: `${selected.qty ? Math.min(100, ((selected.procuredQty || 0) / selected.qty) * 100) : 0}%` }}
                    />
                  </div>
                  {((selected.procuredQty || 0) > 0 || (selected.deliveredQty || 0) > 0) && (
                    <>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <span className="font-medium text-stone-600">Mal Kabul Durumu <span className="text-[10px] text-stone-400">(teslim girişleri)</span></span>
                        <span className={`font-heading font-black ${(selected.deliveredQty || 0) >= selected.qty ? "text-green-700" : "text-amber-700"}`} data-testid="delivery-progress-text">
                          {selected.deliveredQty || 0}/{selected.qty} {unitLabel(selected.unit)} · %{Math.round(((selected.deliveredQty || 0) / (selected.qty || 1)) * 100)}
                          {(selected.deliveredQty || 0) >= selected.qty ? " · Tamamlandı ✓" : ""}
                        </span>
                      </div>
                      <div className="mt-1.5 h-1.5 w-full bg-stone-200">
                        <div
                          className={`h-1.5 transition-all duration-300 ${(selected.deliveredQty || 0) >= selected.qty ? "bg-green-600" : "bg-amber-500"}`}
                          style={{ width: `${Math.min(100, ((selected.deliveredQty || 0) / (selected.qty || 1)) * 100)}%` }}
                        />
                      </div>
                      {(() => {
                        const acc = selected.acceptedQty || 0;
                        const diff = acc - selected.qty;
                        return (
                          <div className="mt-1.5 flex flex-wrap items-center justify-between gap-1 text-[11px]" data-testid="receipt-diff-row">
                            <span className="text-stone-600">
                              Sipariş: <strong>{selected.qty}</strong> · Mal Kabul: <strong className="text-green-700">{acc}</strong>
                            </span>
                            <span
                              className={`border px-1.5 py-px font-medium ${diff === 0 ? "border-green-300 bg-green-50 text-green-700" : diff < 0 ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}
                              data-testid="receipt-diff-badge"
                            >
                              {diff === 0 ? "Fark yok · Tam ✓" : diff < 0 ? `Eksik: ${-diff} ${unitLabel(selected.unit)}` : `Fazla: +${diff} ${unitLabel(selected.unit)}`}
                            </span>
                          </div>
                        );
                      })()}
                      {(selected.deliveryEntries || []).length > 0 && (
                        <ul className="mt-2 space-y-0.5" data-testid="delivery-entries-list">
                          {canReceive && selected.deliveryEntries.some((d) => d.id) && (
                            <li className="mb-1">
                              <button
                                type="button"
                                onClick={clearAllDeliveries}
                                data-testid="clear-all-deliveries-button"
                                className="border border-red-300 bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-700 transition-colors hover:bg-red-600 hover:text-white active:scale-95"
                              >
                                Tümünü Kaldır
                              </button>
                            </li>
                          )}
                          {selected.deliveryEntries.map((d, i) => (
                            <li key={d.id || i} className="flex flex-wrap items-center gap-2 text-[11px] text-stone-600" data-testid={`admin-delivery-entry-${i}`}>
                              {editingDelivery === d.id ? (
                                <>
                                  <Input type="number" min="1" value={editDQty} onChange={(e) => setEditDQty(e.target.value)} data-testid={`edit-delivery-qty-${i}`} className="h-7 w-20 rounded-sm border-stone-300 text-xs" />
                                  <Input type="date" value={editDDate} onChange={(e) => setEditDDate(e.target.value)} data-testid={`edit-delivery-date-${i}`} className="h-7 w-32 rounded-sm border-stone-300 text-xs" />
                                  <Button size="sm" onClick={() => saveDeliveryEdit(d)} disabled={!editDQty || Number(editDQty) <= 0} data-testid={`edit-delivery-save-${i}`} className="h-7 rounded-sm bg-[#1C4E35] px-2 text-[11px] text-white hover:bg-[#143A26]">Kaydet</Button>
                                  <button type="button" onClick={() => setEditingDelivery(null)} className="text-[11px] text-stone-500 hover:text-stone-700">Vazgeç</button>
                                </>
                              ) : (
                                <>
                                  <span className="font-medium text-[#1C4E35]">{d.qty} {unitLabel(selected.unit)}</span>
                                  <span className="text-stone-400">{fmtDate(d.date)}</span>
                                  {d.supplier && <span className="border border-stone-300 bg-white px-1 py-px text-[9px]" title="Teslim eden">{d.supplier}</span>}
                                  {d.status === "pending" ? (
                                    <span className="border border-amber-300 bg-amber-50 px-1 py-px text-[9px] font-medium text-amber-700">Onay bekliyor</span>
                                  ) : (
                                    <span className="border border-green-300 bg-green-50 px-1 py-px text-[9px] font-medium text-green-700">Mal kabul</span>
                                  )}
                                  {canReceive && d.id && d.status === "pending" && (
                                    <button
                                      type="button"
                                      onClick={() => acceptDeliveryEntry(d)}
                                      data-testid={`accept-delivery-${i}`}
                                      className="border border-[#1C4E35] bg-[#1C4E35]/5 px-1.5 py-px text-[10px] font-medium text-[#1C4E35] transition-colors hover:bg-[#1C4E35] hover:text-white active:scale-95"
                                    >
                                      Onayla
                                    </button>
                                  )}
                                  {canReceive && d.id && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => { setEditingDelivery(d.id); setEditDQty(String(d.qty)); setEditDDate(d.date || new Date().toISOString().slice(0, 10)); }}
                                        title="Teslimatı düzelt"
                                        data-testid={`edit-delivery-${i}`}
                                        className="text-stone-400 transition-colors hover:text-[#1C4E35]"
                                      >
                                        <Pencil size={11} />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => deleteDeliveryEntry(d)}
                                        title="Teslimatı sil"
                                        data-testid={`delete-delivery-${i}`}
                                        className="text-stone-400 transition-colors hover:text-red-600"
                                      >
                                        <X size={12} />
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      {canReceive && (selected.directQty || 0) > 0 && (selected.procuredQty || 0) - (selected.deliveredQty || 0) > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-stone-200 pt-2" data-testid="direct-delivery-row">
                          <span className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Depo Teslim Girişi</span>
                          <Input type="date" value={adminDDate} onChange={(e) => setAdminDDate(e.target.value)} data-testid="direct-delivery-date" className="h-8 w-36 rounded-sm border-stone-300 text-xs" />
                          <Input
                            type="number"
                            min="1"
                            max={(selected.procuredQty || 0) - (selected.deliveredQty || 0)}
                            value={adminDQty}
                            onChange={(e) => setAdminDQty(e.target.value)}
                            placeholder={`en çok ${(selected.procuredQty || 0) - (selected.deliveredQty || 0)}`}
                            data-testid="direct-delivery-qty"
                            className={`h-8 w-28 rounded-sm text-xs ${Number(adminDQty) > (selected.procuredQty || 0) - (selected.deliveredQty || 0) ? "border-red-500 focus-visible:ring-red-500" : "border-stone-300"}`}
                          />
                          <Button
                            size="sm"
                            onClick={saveDirectDelivery}
                            disabled={!adminDQty || Number(adminDQty) <= 0 || Number(adminDQty) > (selected.procuredQty || 0) - (selected.deliveredQty || 0)}
                            data-testid="direct-delivery-save"
                            className="h-8 rounded-sm bg-amber-600 text-xs text-white hover:bg-amber-700 active:scale-95"
                          >
                            Teslim Gir
                          </Button>
                          {Number(adminDQty) > (selected.procuredQty || 0) - (selected.deliveredQty || 0) && (
                            <p className="w-full border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-600" data-testid="direct-delivery-qty-error">
                              Girilen miktar kalan adedi ({(selected.procuredQty || 0) - (selected.deliveredQty || 0)}) aşamaz.
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {(selected.supplierBreakdown || []).length > 0 && (
                    <div className="mt-2 space-y-1 border-t border-stone-200 pt-2" data-testid="supplier-breakdown">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Tedarikçi Dağılımı</p>
                      {selected.supplierBreakdown.map((b) => (
                        <div key={b.supplierId} className="text-[11px]" data-testid={`supplier-alloc-${b.supplierId}`}>
                          <div className="flex items-center gap-2">
                            <span className="border border-stone-300 bg-white px-1.5 py-px font-medium">{b.supplier}</span>
                            {b.direct && <span className="border border-sky-400 bg-sky-50 px-1.5 py-px text-[10px] font-medium text-sky-700">Doğrudan</span>}
                            <span className="text-stone-600">
                              Atanan <b className="font-heading text-[#1C4E35]">{b.qty}</b> · Teslim{" "}
                              <b className={`font-heading ${(b.delivered || 0) >= b.qty ? "text-green-700" : "text-amber-700"}`}>{b.delivered || 0}</b>
                              {b.qty - (b.delivered || 0) > 0 && <span className="text-stone-400"> · kalan {b.qty - (b.delivered || 0)}</span>}
                            </span>
                            {canAssign && (
                            <button
                              type="button"
                              onClick={() => removeSupplierAlloc(b)}
                              title="Atamayı kaldır"
                              data-testid={`remove-alloc-${b.supplierId}`}
                              className="ml-auto text-red-500 transition-colors hover:text-red-700"
                            >
                              <X size={12} />
                            </button>
                            )}
                          </div>
                          <div className="mt-1 h-1 w-full bg-stone-200">
                            <div
                              className={`h-1 transition-all duration-500 ${(b.delivered || 0) >= b.qty ? "bg-green-600" : "bg-amber-500"}`}
                              style={{ width: `${Math.min(100, ((b.delivered || 0) / (b.qty || 1)) * 100)}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {canAssign && suppliers.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-stone-200 pt-2" data-testid="qty-assign-row">
                      <Input
                        type="number"
                        min="1"
                        value={assignQty}
                        onChange={(e) => setAssignQty(e.target.value)}
                        placeholder={selected.qty - (selected.procuredQty || 0) > 0 ? `örn. ${selected.qty - (selected.procuredQty || 0)}` : "miktar"}
                        data-testid="qty-assign-input"
                        className="h-8 w-28 rounded-sm border-stone-300 text-xs"
                      />
                      <Select value={assignSupplier} onValueChange={setAssignSupplier}>
                        <SelectTrigger className="h-8 min-w-[140px] flex-1 rounded-sm border-stone-300 text-xs" data-testid="qty-assign-supplier-select">
                          <SelectValue placeholder="Tedarikçi seçin" />
                        </SelectTrigger>
                        <SelectContent className="max-h-64">
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-medium text-stone-700" data-testid="qty-assign-direct-label">
                        <Checkbox
                          checked={assignDirect}
                          onCheckedChange={(v) => { setAssignDirect(!!v); setAssignSupplier(""); setAssignVendor(""); }}
                          data-testid="qty-assign-direct-checkbox"
                          className="h-3.5 w-3.5 border-stone-400 data-[state=checked]:border-sky-600 data-[state=checked]:bg-sky-600"
                        />
                        Doğrudan Satınalma
                      </label>
                      <Button
                        size="sm"
                        onClick={requestQtyAssign}
                        disabled={(!assignSupplier && !(assignDirect && assignVendor.trim())) || !assignQty || Number(assignQty) <= 0}
                        data-testid="qty-assign-confirm"
                        className="h-8 rounded-sm bg-[#1C4E35] text-xs text-white hover:bg-[#143A26] active:scale-95"
                      >
                        {assignDirect ? "Kaydet" : "Ata"}
                      </Button>
                      {Number(assignQty) > Math.max(0, selected.qty - (selected.procuredQty || 0)) && Number(assignQty) > 0 && (
                        <span className="border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" data-testid="qty-assign-surplus-hint">
                          Sipariş fazlası: +{Number(assignQty) - Math.max(0, selected.qty - (selected.procuredQty || 0))} adet
                        </span>
                      )}
                      {assignDirect && (
                        <div className="relative w-full">
                          <Input
                            value={assignVendor}
                            onChange={(e) => { setAssignVendor(e.target.value); setShowVendorList(true); if (e.target.value) setAssignSupplier(""); }}
                            onFocus={() => setShowVendorList(true)}
                            onBlur={() => setTimeout(() => setShowVendorList(false), 150)}
                            placeholder="veya satıcı adını serbestçe yazın"
                            data-testid="qty-assign-vendor-input"
                            className="h-8 w-full rounded-sm border-stone-300 text-xs"
                          />
                          {showVendorList && assignVendor.trim() && (() => {
                            const q = assignVendor.trim().toLocaleLowerCase("tr");
                            const matches = vendorSuggestions.filter((v) => v.toLocaleLowerCase("tr").startsWith(q) && v.toLocaleLowerCase("tr") !== q);
                            return matches.length > 0 ? (
                              <div className="absolute left-0 right-0 top-full z-50 border border-stone-300 bg-white shadow-md" data-testid="vendor-suggestions">
                                {matches.slice(0, 6).map((v) => (
                                  <button
                                    key={v}
                                    type="button"
                                    onMouseDown={(e) => { e.preventDefault(); setAssignVendor(v); setShowVendorList(false); }}
                                    data-testid={`vendor-suggestion-${v.replace(/\s+/g, "_")}`}
                                    className="block w-full px-3 py-1.5 text-left text-xs text-stone-700 hover:bg-stone-100"
                                  >
                                    {v}
                                  </button>
                                ))}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}
                      {Number(assignQty) > selected.qty - (selected.procuredQty || 0) && Number(assignQty) > 0 && (
                        <p className="w-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700" data-testid="qty-assign-surplus-note">
                          Kalan adedi ({Math.max(0, selected.qty - (selected.procuredQty || 0))}) aşıyorsunuz — fazlası sipariş fazlası olarak kaydedilecek.
                        </p>
                      )}
                    </div>
                  )}
                  {canAssign && <p className="mt-1 text-[10px] text-stone-500">Miktar girerek tedarikçilere bölüştürebilir veya aşağıdaki "Tedarik" kutusundan satır bazlı işaretleyebilirsiniz.</p>}
                </div>
              )}
              {(() => {
                const vmap = new Map();
                (status === "CANCELLED" ? selected.orderers : selected.orderers.filter((x) => x.status !== "CANCELLED")).forEach((o) => {
                  const k = o.variant || "Belirtilmemiş";
                  const v = vmap.get(k) || { qty: 0, count: 0 };
                  v.qty += o.qty || 0;
                  v.count += 1;
                  vmap.set(k, v);
                });
                const entries = [...vmap.entries()].sort((a, b) => b[1].qty - a[1].qty);
                if (entries.length <= 1 && entries[0]?.[0] === "Belirtilmemiş") return null;
                return (
                  <div className="border border-stone-200 bg-stone-50 p-3" data-testid="product-variant-breakdown">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">Renk / Varyant Dağılımı</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {entries.map(([variant, v]) => (
                        <div key={variant} className="flex items-center gap-2 border border-stone-200 bg-white px-2.5 py-1.5" data-testid={`variant-stat-${variant}`}>
                          <VariantBadge variant={variant === "Belirtilmemiş" ? null : variant} />
                          {variant === "Belirtilmemiş" && <span className="text-[11px] text-stone-500">Belirtilmemiş</span>}
                          <span className="font-heading text-sm font-bold text-[#1C4E35]">{v.qty} {unitLabel(selected.unit)}</span>
                          <span className="text-[10px] text-stone-400">({v.count} sipariş)</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {!isLimited && (
              <DetailActions
                pdfPath="/orders/by-product/pdf"
                pdfParams={{ sku: selected.sku, space_type: spaceType || undefined, status: status !== "__all__" ? status : undefined, variant: selected.variant || undefined, zone: zone || undefined }}
                emailPath="/orders/by-product/email"
                emailPayload={{ sku: selected.sku, space_type: spaceType || "", status: status !== "__all__" ? status : "", variant: selected.variant || "", zone: zone || "" }}
                filename={`cop31_urun_${selected.sku}.pdf`}
                testPrefix="product"
                historyScope={`product|${selected.sku}|${selected.variant || ""}`}
                onMailSent={() => setMailRefresh((c) => c + 1)}
              />
              )}
              {!isLimited && <EmailHistory scope={`product|${selected.sku}|${selected.variant || ""}`} refresh={mailRefresh} testPrefix="product" />}
              <Table>
                <TableHeader>
                  <TableRow>
                    {canAssign && (
                      <TableHead className="w-[80px] text-xs">
                        <span className="flex items-center gap-1.5">
                          <Checkbox
                            checked={selected.orderers.length > 0 && selected.orderers.every((x) => x.procured)
                              ? true
                              : selected.orderers.some((x) => x.procured)
                              ? "indeterminate"
                              : false}
                            onCheckedChange={toggleAllProcured}
                            data-testid="procure-check-all"
                            title="Tümünü seç / kaldır"
                            className="border-stone-400 data-[state=checked]:border-[#1C4E35] data-[state=checked]:bg-[#1C4E35]"
                          />
                          Tedarik
                        </span>
                      </TableHead>
                    )}
                    <SortHead k="plot" label="Stand No" />
                    <SortHead k="organisation" label="Sipariş Veren" />
                    <SortHead k="variant" label="Renk / Seçim" />
                    <SortHead k="ref" label="Referans" />
                    <SortHead k="status" label="Durum" />
                    <SortHead k="qty" label="Miktar" right />
                    <SortHead k="lineDelivered" label="Teslimat" right />
                    {!isViewer && <SortHead k="lineTotal" label="Tutar" right />}
                    <SortHead k="placedAt" label="Tarih" right />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedOrderers.map((o, i) => (
                    <TableRow key={`${o.ref}-${i}`} className={o.procured ? "bg-green-50/60" : ""}>
                      {canAssign && (
                        <TableCell className="py-2">
                          <Checkbox
                            checked={!!o.procured}
                            onCheckedChange={() => toggleProcured(o)}
                            data-testid={`procure-check-${i}`}
                            className="border-stone-400 data-[state=checked]:border-[#1C4E35] data-[state=checked]:bg-[#1C4E35]"
                          />
                          {o.procured && o.supplier && (
                            <p className="mt-1 max-w-[70px] truncate text-[9px] text-[#1C4E35]" title={o.supplier} data-testid={`procure-supplier-${i}`}>{o.supplier}</p>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="py-2 text-xs font-semibold text-[#1C4E35]" data-testid={`orderer-plot-${i}`}>{o.plot || "—"}</TableCell>
                      <TableCell className="py-2">
                        <p className="flex items-center gap-1.5 text-sm font-medium">
                          {o.organisation}
                          {o.manual && <span className="border border-violet-300 bg-violet-50 px-1 py-px text-[9px] font-bold text-violet-700" title="Manuel ekleme" data-testid={`orderer-manual-${i}`}>M</span>}
                        </p>
                        <p className="text-[11px] text-stone-400">{o.space || ""}</p>
                      </TableCell>
                      <TableCell className="py-2">
                        <VariantBadge variant={o.variant} />
                      </TableCell>
                      <TableCell className="py-2 text-xs">{o.ref}</TableCell>
                      <TableCell className="py-2">
                        <span className={`border px-2 py-0.5 text-[10px] font-medium ${statusClass(o.status)}`}>{statusLabel(o.status)}</span>
                      </TableCell>
                      <TableCell className="py-2 text-right text-sm font-medium">
                        {o.qty} {unitLabel(selected.unit)}
                        {o.detail && <p className="text-[10px] font-normal text-stone-500" data-testid={`orderer-detail-${i}`}>{detailTr(o.detail)}</p>}
                      </TableCell>
                      <TableCell className="py-2 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {canReceive && (
                            <Checkbox
                              checked={(o.qty || 0) > 0 && (o.lineDelivered || 0) >= (o.qty || 0)}
                              onCheckedChange={(v) => fullDeliverToggle(o, !!v)}
                              title="%100 teslim edildi olarak işaretle"
                              data-testid={`line-full-deliver-${i}`}
                              className="border-green-600 data-[state=checked]:border-green-700 data-[state=checked]:bg-green-700"
                            />
                          )}
                          <div>
                            <p className={`text-sm font-medium ${(o.lineDelivered || 0) > 0 && (o.lineDelivered || 0) >= (o.qty || 0) ? "text-green-700" : (o.lineDelivered || 0) > 0 ? "text-amber-700" : "text-stone-300"}`} data-testid={`line-delivered-${i}`}>
                              {o.lineDelivered || 0}
                            </p>
                            {(o.lineDelivered || 0) > 0 && ((o.qty || 0) - (o.lineDelivered || 0) > 0 ? (
                              <p className="text-[10px] font-semibold text-red-600" data-testid={`line-remaining-${i}`}>kalan {Math.round(((o.qty || 0) - (o.lineDelivered || 0)) * 100) / 100}</p>
                            ) : (
                              <p className="text-[10px] font-semibold text-green-600">tamam ✓</p>
                            ))}
                          </div>
                          {canReceive && <LineDeliveryPopover o={o} unit={selected.unit} idx={i} onAdd={addLineDelivery} onDelete={deleteLineDelivery} />}
                        </div>
                      </TableCell>
                      {!isViewer && <TableCell className="py-2 text-right text-sm">{fmtMoney(o.lineTotal, selected.currency)}</TableCell>}
                      <TableCell className="py-2 text-right text-xs text-stone-500">{fmtDate(o.placedAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!procureDialog} onOpenChange={(v) => !v && setProcureDialog(null)}>
        <DialogContent aria-describedby={undefined} className="rounded-sm bg-white sm:max-w-sm" data-testid="procure-supplier-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold tracking-tight">Tedarikçi Seçin</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500">
            {procureDialog?.mode === "all"
              ? "Tüm satırlar seçtiğiniz tedarikçiye sipariş verildi olarak işaretlenecek."
              : `${procureDialog?.line?.organisation || ""} siparişi hangi tedarikçiye verildi?`}
          </p>
          <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-stone-700" data-testid="direct-buy-label">
            <Checkbox
              checked={directBuy}
              onCheckedChange={(v) => { setDirectBuy(!!v); setProcureSupplier(""); setFreeVendor(""); }}
              data-testid="direct-buy-checkbox"
            />
            Doğrudan Satınalma
          </label>
          <Select value={procureSupplier} onValueChange={setProcureSupplier}>
            <SelectTrigger className="rounded-sm border-stone-300" data-testid="procure-supplier-select">
              <SelectValue placeholder="Tedarikçi seçin" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {directBuy && (
            <div className="relative">
              <Input
                value={freeVendor}
                onChange={(e) => { setFreeVendor(e.target.value); setShowFreeVendorList(true); if (e.target.value) setProcureSupplier(""); }}
                onFocus={() => setShowFreeVendorList(true)}
                onBlur={() => setTimeout(() => setShowFreeVendorList(false), 150)}
                placeholder="veya satıcı adını serbestçe yazın"
                data-testid="direct-buy-vendor-input"
                className="rounded-sm border-stone-300"
              />
              {showFreeVendorList && freeVendor.trim() && (() => {
                const q = freeVendor.trim().toLocaleLowerCase("tr");
                const matches = vendorSuggestions.filter((v) => v.toLocaleLowerCase("tr").startsWith(q) && v.toLocaleLowerCase("tr") !== q);
                return matches.length > 0 ? (
                  <div className="absolute left-0 right-0 top-full z-50 border border-stone-300 bg-white shadow-md" data-testid="dialog-vendor-suggestions">
                    {matches.slice(0, 6).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); setFreeVendor(v); setShowFreeVendorList(false); }}
                        data-testid={`dialog-vendor-suggestion-${v.replace(/\s+/g, "_")}`}
                        className="block w-full px-3 py-1.5 text-left text-xs text-stone-700 hover:bg-stone-100"
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setProcureDialog(null)} className="rounded-sm border-stone-300">Vazgeç</Button>
            <Button
              onClick={confirmProcure}
              disabled={!procureSupplier && !(directBuy && freeVendor.trim())}
              data-testid="procure-supplier-confirm"
              className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95"
            >
              {directBuy ? "Kaydet" : "İşaretle"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!catAssign} onOpenChange={(v) => !v && setCatAssign(null)}>
        <DialogContent aria-describedby={undefined} className="rounded-sm bg-white sm:max-w-md" data-testid="category-assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-bold tracking-tight">Kategoriyi Tedarikçiye Ata</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-600">
            <span className="font-semibold text-[#1C4E35]">{catAssign?.category}</span> kategorisinde atanacak ürünleri seçin.
            Daha önce başka tedarikçiye atanmış satırlara dokunulmaz.
          </p>
          <div className="max-h-60 overflow-y-auto border border-stone-200 px-3 py-1" data-testid="category-assign-products">
            {(catAssign?.products || []).map((p) => {
              const k = `${p.sku}|${p.variant || ""}`;
              const remaining = p.qty - (p.procuredQty || 0);
              return (
                <label key={k} className="flex cursor-pointer items-center gap-2 border-b border-stone-100 py-1.5 last:border-0">
                  <Checkbox
                    checked={!!catChecked[k]}
                    onCheckedChange={(v) => setCatChecked((c) => ({ ...c, [k]: !!v }))}
                    data-testid={`cat-assign-check-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                  />
                  <span className="flex-1 truncate text-sm">{p.name}{p.variant ? ` — ${p.variant}` : ""}</span>
                  <span className={`shrink-0 text-[11px] ${remaining > 0 ? "text-stone-500" : remaining < 0 ? "text-amber-600" : "text-green-700"}`}>
                    {remaining > 0 ? `Kalan ${remaining}` : remaining < 0 ? `Fazla +${-remaining}` : "Tamamı atanmış"}
                  </span>
                  {(p.procuredQty || 0) > 0 && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); removeProductAssign(p); }}
                      title="Bu ürünün tüm tedarikçi atamalarını kaldır"
                      data-testid={`cat-unassign-${p.sku}${p.variant ? `-${p.variant.replace(/\s+/g, "_")}` : ""}`}
                      className="shrink-0 border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-600 transition-colors hover:bg-red-100 active:scale-95"
                    >
                      Kaldır
                    </button>
                  )}
                </label>
              );
            })}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button type="button" onClick={() => setCatChecked((c) => Object.fromEntries(Object.keys(c).map((k) => [k, true])))} className="text-[#1C4E35] underline" data-testid="cat-assign-select-all">Tümünü seç</button>
            <button type="button" onClick={() => setCatChecked((c) => Object.fromEntries(Object.keys(c).map((k) => [k, false])))} className="text-stone-500 underline" data-testid="cat-assign-select-none">Temizle</button>
            {(catAssign?.products || []).some((p) => (p.procuredQty || 0) > 0) && (
              <button
                type="button"
                onClick={removeAllCatAssigns}
                data-testid="cat-assign-remove-all"
                className="ml-auto border border-red-300 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600 transition-colors hover:bg-red-100 active:scale-95"
              >
                Tümünü Kaldır
              </button>
            )}
          </div>
          <Select value={catSupplier} onValueChange={setCatSupplier}>
            <SelectTrigger className="rounded-sm border-stone-300" data-testid="category-assign-supplier-select">
              <SelectValue placeholder="Tedarikçi seçin" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCatAssign(null)} className="rounded-sm border-stone-300">Vazgeç</Button>
            <Button
              onClick={confirmCatAssign}
              disabled={!catSupplier || !Object.values(catChecked).some(Boolean)}
              data-testid="category-assign-confirm"
              className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95"
            >
              Ata
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmBox} onOpenChange={(v) => !v && setConfirmBox(null)}>
        <AlertDialogContent className="rounded-sm border-stone-200" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-base font-bold tracking-tight">{confirmBox?.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line text-sm text-stone-600">{confirmBox?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-stone-300" data-testid="confirm-dialog-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const fn = confirmBox?.onConfirm; setConfirmBox(null); fn && fn(); }}
              data-testid="confirm-dialog-ok"
              className="rounded-sm bg-red-600 text-white hover:bg-red-700 active:scale-95"
            >
              Evet, devam et
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {lightbox && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-6"
          onClick={() => setLightbox(null)}
          data-testid="image-lightbox"
        >
          <div className="relative max-h-[88vh] w-full max-w-3xl bg-white p-4" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              data-testid="lightbox-close"
              className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center bg-[#0A0D0B] text-white transition-colors hover:bg-[#1C4E35]"
            >
              <X size={16} />
            </button>
            <img src={mediaUrl(lightbox.image)} alt={lightbox.name} style={beigeStyle(lightbox.variant)} className="max-h-[70vh] w-full object-contain" data-testid="lightbox-image" />
            <p className="mt-3 text-center text-sm font-semibold text-[#0A0D0B]">{lightbox.name}</p>
            <p className="text-center text-[11px] uppercase tracking-wider text-stone-400">{lightbox.sku}</p>
          </div>
        </div>
      )}
    </div>
  );
}
